// Textos da UI FiberHome / dirigida por capacidades (docs/ImplementacaoFiberHome, Fase 08):
// cadastro da OLT, painel "Capacidades & avisos", autorização por classe/VLAN/banda, serviços da ONU,
// status com motivo, ações automáticas e predefinições. Vive fora de i18n.js só para não inflar aquele
// arquivo; é ligado em DICT.<lang>.app.fh e lido por `useApp().t.app.fh`.
//
// Vocabulário: "banda do serviço (FIX/ASSURE/MAX)" — a OLT guarda uma banda por serviço e a direção
// (subida/descida) ainda não está confirmada; por isso nada aqui diz "upload/download" da banda ao vivo.

const pt = {
  olt: {
    telnet_warning: 'Telnet e SNMP v2c trafegam sem criptografia — use VPN/túnel (ou SSH, se o equipamento oferecer) entre o OLTM e a OLT.',
    ssh_snmp_warning: 'O SNMP v2c continua sem criptografia mesmo com SSH — use VPN/túnel entre o OLTM e a OLT.',
    dialect_label: 'Dialeto da CLI',
    dialect_auto_hint: 'Detectado automaticamente na identificação (Testar conexão). Não é preciso escolher.',
    dialect_detected_hint: 'Detectado pelo sistema na identificação da OLT.',
    rw_label: 'Comunidade SNMP de escrita',
    rw_configured: 'configurada',
    rw_will_set: 'será gravada ao salvar',
    rw_will_remove: 'será removida ao salvar',
    rw_remove_action: 'Remover community de escrita',
    rw_remove_undo: 'Desfazer remoção',
    rw_remove_confirm: 'Remover a community de escrita? Renomear, reiniciar e remover ONU passarão a usar o CLI (mais lento). A remoção só vale ao salvar.',
    rw_missing: 'não configurada',
    rw_hint: 'O valor nunca é exibido. Sem ela, renomear, reiniciar e remover ONU usam o CLI (mais lento).',
    rw_form_hint: 'Opcional. Usada para renomear, reiniciar e remover ONU por SNMP; sem ela o CLI é usado (mais lento). Não é criada na OLT.',
    ro_form_hint: 'A comunidade de leitura já existente na OLT (não é criada automaticamente).',
    model_unsupported: 'não suportado ainda',
    model_unsupported_error: 'Este modelo FiberHome (AN6000) ainda não é suportado — escolha um AN5516/AN5116.',
    identity_title: 'Equipamento identificado',
    identity_model: 'Modelo',
    identity_family: 'Família',
    identity_firmware: 'Firmware',
    identity_dialect: 'Dialeto da CLI',
    identity_serial: 'Nº de série',
    identity_caps_after_save: 'As capacidades do equipamento aparecem depois de salvar a OLT (Testar conexão na tela de edição).',
    model_validated: 'validado',
    model_not_validated: 'compatível, não validado',
    model_not_validated_hint: 'Este modelo usa o mesmo driver do AN5516-01, mas nunca foi validado num equipamento real. Teste a conexão e acompanhe o primeiro poll antes de autorizar ONUs.',
    pon_only_gpon: 'A integração FiberHome v1 suporta só GPON (EPON e XG-PON ficam para uma versão futura).',
    ssh_not_validated: 'SSH (não validado na FiberHome)',
    ssh_not_validated_hint: 'O driver aceita SSH, mas ele nunca foi validado numa OLT FiberHome real (o servidor SSH costuma vir desligado). Prefira Telnet num túnel/VPN.',
    enable_hint: 'O mesmo usuário e senha entram no modo privilegiado (enable) — a OLT FiberHome usa a senha de login no enable.',
    identity_replaces: 'Ao salvar, o modelo e o firmware identificados substituem o que foi escolhido acima.',
    cli_probe_label: 'Login na CLI',
    shell_unsupported: 'O terminal interativo ainda não é suportado para FiberHome (ele contornaria a lista de comandos permitidos). Use as ações da ONU/OLT e a auditoria de comandos.',
  },
  cap: {
    authorize: 'Autorizar', rename: 'Renomear', reboot: 'Reiniciar', disable: 'Desativar/Ativar', delete: 'Excluir',
    vlan: 'VLAN', bandwidth: 'Banda', move: 'Mover', wan: 'WAN', factory_reset: 'Restaurar padrões',
  },
  panel: {
    title: 'Capacidades & avisos',
    reload: 'Atualizar',
    retry: 'Tentar de novo',
    load_error: 'Não foi possível ler as capacidades desta OLT. Os botões continuam como de costume.',
    model: 'Modelo de provisionamento',
    model_intent: 'por intenção (whitelist + serviço, sem perfis de linha/serviço)',
    model_profiles: 'por perfis (line-profile / service-profile)',
    classes: 'Classes de ONU',
    onuid_by_olt: 'O ONU-ID é escolhido pela OLT.',
    remove_clears: 'Remover a ONU apaga o serviço e o histórico dela na OLT.',
    needs_save: 'As alterações só persistem depois de um save — o sistema agenda um ~5 min após a última escrita.',
    no_mass_ops: 'Operações em massa (reiniciar PON/placa, ações em todas as ONUs) não existem para esta OLT.',
    no_warnings: 'Nenhum aviso no estado atual da OLT.',
    board_serial: 'Nº de série',
    board_roles: { master: 'Principal (master)', standby: 'Reserva (standby)' },
    board_reboot_blocked: 'Reiniciar placa não é feito por aqui nesta OLT.',
    auth_mode: 'Autenticação',
    auth_modes: {
      whitelist: 'Whitelist por serial', no_auth: 'Sem autenticação (no_auth)', secret: 'Com senha/LOID',
      other: 'Outro modo', unknown: 'Não lido',
    },
  },
  warn: {
    pon_no_auth: { title: 'PON(s) em no_auth', action: 'Nessas PONs as ONUs se autorizam sozinhas e o provisionamento controlado fica indisponível. Coloque-as no modo de whitelist por serial pelo equipamento — o sistema não altera o modo da PON.' },
    pon_auth_needs_secret: { title: 'PON(s) em modo com senha/LOID', action: 'Ainda não suportado: a autorização por serial não se aplica nessas PONs.' },
    auto_save_off: { title: 'auto_save desligado na OLT', action: 'As alterações só persistem depois de um save, que o sistema agenda (~5 min após a última escrita).' },
    onu_alarms_off: { title: 'Alarmes de desativação de ONU desligados na OLT', action: 'Quedas não geram alarme no equipamento; o sistema detecta pela coleta.' },
    telnet_without_ssh: { title: 'Acesso CLI por Telnet (sem criptografia)', action: 'Prefira SSH quando o equipamento oferecer, ou use VPN/túnel.' },
    snmp_rw_missing: { title: 'Comunidade SNMP de escrita ausente', action: 'Renomear, reiniciar e remover ONU usam o CLI (mais lento). Cadastre a comunidade em Editar OLT.' },
    pending_save: { title: 'Configuração ainda não salva na OLT', action: 'Há escritas recentes sem save. O sistema salva sozinho ~5 min após a última escrita.' },
    driver_warning: { title: 'Aviso do driver', action: '' },
  },
  auth: {
    class_label: 'Classe da ONU',
    class_names: { bridge: 'Bridge', hgu: 'HGU (roteada)' },
    class_help: { bridge: 'Tipo de autorização {type}: a ONU repassa a VLAN do serviço.', hgu: 'Tipo de autorização {type}: ONU com roteador/Wi-Fi próprio.' },
    class_pick: 'Escolha a classe — define o tipo de autorização gravado na OLT.',
    class_detected: 'Sugerida pelo equipamento detectado no autofind: {class}.',
    vlan_label: 'VLAN do serviço',
    vlan_none: 'Escolha a VLAN…',
    vlan_help: 'Na FiberHome a VLAN é o próprio serviço da ONU. Só aparecem as VLANs liberadas para a PON desta ONU.',
    vlan_load_error: 'Não foi possível carregar as VLANs da OLT — não dá para autorizar até carregar.',
    mode_label: 'Modo da VLAN',
    mode_help: 'Tag: a OLT marca o tráfego com a VLAN. Transparente: passa como veio.',
    mode_names: { tag: 'Tag', transparent: 'Transparente' },
    mode_default: 'Padrão da classe',
    mode_default_unknown: 'Padrão da classe (escolha a classe)',
    cos_label: 'COS (avançado)',
    cos_help: 'Prioridade 802.1p de 0 a 7. Em branco usa o padrão da classe.',
    cos_default: 'padrão: {n}',
    cos_default_unknown: 'padrão da classe',
    port_speed_label: 'Velocidade da porta Ethernet',
    port_speed_help: 'Avançado. Em branco não mexe na porta da ONU.',
    port_speed_keep: 'Não alterar',
    bw_download_label: 'Banda — perfil de velocidade (download)',
    bw_upload_label: 'Banda — perfil de velocidade (upload)',
    bw_note: 'A OLT guarda uma única "banda do serviço" (FIX/ASSURE/MAX): com dois perfis diferentes vale o menor dos dois. Em branco usa o padrão do tenant.',
    bw_load_error: 'Não foi possível carregar os perfis de velocidade — o padrão do tenant ainda é aplicado pelo servidor.',
    onuid_label: 'ONU-ID',
    onuid_auto: 'Automático',
    onuid_manual: 'Manual',
    onuid_auto_help: 'A OLT escolhe o primeiro ONU-ID livre da PON (recomendado).',
    onuid_manual_help: 'Use só se precisar de um ONU-ID específico (1 a 128). Se estiver em uso, a OLT recusa e nada é gravado.',
    save_note: 'A configuração foi aplicada e confirmada na OLT. O save (persistência) é agendado pelo sistema ~5 min depois da última escrita.',
    preset_hidden: '{n} predefinição(ões) de outro fabricante não aparecem: esta OLT é {vendor}.',
  },
  onu: {
    status_reasons: {
      los: 'LOS (perda de sinal óptico)', dyinggasp: 'Dying-gasp (queda de energia)', offline: 'Offline',
      rebooting: 'reiniciando…', 'auth-fail': 'Falha de autenticação', deauthorized: 'Desativada', other: 'Estado fora do padrão',
    },
    equipment: 'Equipamento',
    auth_type: 'Tipo de autorização',
    auth_type_unknown: 'ainda não lido da OLT',
    live_bw_note: 'A OLT atualiza os contadores de tráfego a cada {min} min — a banda ao vivo avança em degraus, não em tempo real.',
    live_bw_waiting: 'Aguardando o próximo degrau do contador da OLT (até {min} min) — a banda aparece quando ele chegar.',
    delete_prompt: 'Remover apaga o serviço e o histórico da ONU na OLT. Confirmar?',
    delete_warning: 'Excluir remove o serviço e o histórico da ONU na OLT; para voltar é preciso autorizar de novo.',
    move_unsupported: 'Mover ONU não existe nesta OLT (remover apaga o serviço): é uma nova autorização.',
    replace_unsupported: 'Trocar o serial não existe nesta OLT: remova a ONU e autorize o novo serial.',
    save_later: 'Alterações nesta OLT são salvas no equipamento pelo sistema ~5 min depois da última escrita.',
  },
  services: {
    section: 'Serviços (VLAN / banda)',
    open: 'Alterar VLAN / banda',
    title: 'Serviços da ONU',
    heading: 'Alterar VLAN / banda',
    loading: 'Lendo os serviços da ONU…',
    load_error: 'Não foi possível ler os serviços da ONU.',
    not_supported: 'Esta OLT usa service-ports (perfis), não serviços por intenção.',
    class_inferred: 'inferida do tipo',
    type_line: 'Tipo de autorização {type} · classe {class}',
    no_service_read: 'Nenhum serviço lido da OLT ainda para esta ONU — rode um poll da OLT.',
    port: 'Porta',
    vlan: 'VLAN do serviço',
    vlan_none: 'Escolha a VLAN…',
    vlan_load_error: 'Não foi possível carregar as VLANs da OLT.',
    vlan_out_of_rule: 'fora da regra da PON',
    vlan_unknown: 'fora do cadastro da OLT',
    mode: 'Modo da VLAN',
    mode_none: 'Escolha o modo…',
    cos: 'COS',
    bw_title: 'Banda do serviço (FIX/ASSURE/MAX)',
    bw_short: 'Banda',
    bw_note: 'Em kbit/s. A OLT guarda uma banda por serviço; a direção (subida/descida) ainda não foi confirmada, por isso não é separada aqui.',
    bw_unsupported: 'Esta OLT não declara suporte a alterar a banda.',
    save_later: 'A OLT só persiste a alteração depois de um save, que o sistema agenda ~5 min após a última escrita.',
    done: 'Serviços aplicados e confirmados na OLT.',
    saving: 'Aplicando…',
    save: 'Aplicar',
    close: 'Fechar',
    applied_toast: 'Serviços da ONU {sn} aplicados',
  },
  auto: {
    move: 'Auto-Mover não existe em OLT FiberHome ({olts}): remover a ONU apaga o serviço e o histórico. Use a autorização.',
    resync: 'Auto-Ressincronizar não existe em OLT FiberHome ({olts}): refazer a configuração é uma nova autorização.',
  },
  preset: {
    vendor_label: 'Fabricante',
    vendor_help: 'A predefinição só aparece em OLT do mesmo fabricante (Huawei usa perfis de linha/serviço; FiberHome usa classe, VLAN e modo).',
    class_help: 'Em branco: a classe vem do tipo de ONU ou é escolhida ao autorizar.',
    class_auto: 'Definir ao autorizar',
    vlan_help: 'Obrigatória para autorizar por predefinição: na FiberHome a VLAN é o serviço da ONU.',
    cos_invalid: 'COS inválido — use um inteiro de 0 a 7.',
  },
};

const en = {
  olt: {
    telnet_warning: 'Telnet and SNMP v2c travel unencrypted — use a VPN/tunnel (or SSH, if the device offers it) between OLTM and the OLT.',
    ssh_snmp_warning: 'SNMP v2c stays unencrypted even over SSH — use a VPN/tunnel between OLTM and the OLT.',
    dialect_label: 'CLI dialect',
    dialect_auto_hint: 'Detected automatically at identification (Test connection). No need to pick one.',
    dialect_detected_hint: 'Detected by the system when the OLT was identified.',
    rw_label: 'SNMP write community',
    rw_configured: 'configured',
    rw_will_set: 'will be stored on save',
    rw_will_remove: 'will be removed on save',
    rw_remove_action: 'Remove write community',
    rw_remove_undo: 'Undo removal',
    rw_remove_confirm: 'Remove the write community? Renaming, rebooting and removing an ONU will use the CLI (slower). Removal only applies on save.',
    rw_missing: 'not configured',
    rw_hint: 'The value is never shown. Without it, renaming, rebooting and removing an ONU use the CLI (slower).',
    rw_form_hint: 'Optional. Used to rename, reboot and remove ONUs over SNMP; without it the CLI is used (slower). It is not created on the OLT.',
    ro_form_hint: 'The read community that already exists on the OLT (it is not created automatically).',
    model_unsupported: 'not supported yet',
    model_unsupported_error: 'This FiberHome model (AN6000) is not supported yet — pick an AN5516/AN5116.',
    identity_title: 'Equipment identified',
    identity_model: 'Model',
    identity_family: 'Family',
    identity_firmware: 'Firmware',
    identity_dialect: 'CLI dialect',
    identity_serial: 'Serial no.',
    identity_caps_after_save: 'The equipment capabilities show up after the OLT is saved (Test connection on the edit screen).',
    model_validated: 'validated',
    model_not_validated: 'compatible, not validated',
    model_not_validated_hint: 'This model uses the same driver as the AN5516-01 but was never validated on real equipment. Test the connection and watch the first poll before authorizing ONUs.',
    pon_only_gpon: 'The FiberHome v1 integration supports GPON only (EPON and XG-PON are planned for a future version).',
    ssh_not_validated: 'SSH (not validated on FiberHome)',
    ssh_not_validated_hint: 'The driver accepts SSH, but it was never validated on a real FiberHome OLT (the SSH server usually ships disabled). Prefer Telnet over a tunnel/VPN.',
    enable_hint: 'The same user and password enter privileged mode (enable) — the FiberHome OLT uses the login password for enable.',
    identity_replaces: 'On save, the identified model and firmware replace what was chosen above.',
    cli_probe_label: 'CLI login',
    shell_unsupported: 'The interactive terminal is not supported for FiberHome yet (it would bypass the allowed-command list). Use the ONU/OLT actions and the command audit.',
  },
  cap: {
    authorize: 'Authorize', rename: 'Rename', reboot: 'Reboot', disable: 'Disable/Enable', delete: 'Delete',
    vlan: 'VLAN', bandwidth: 'Bandwidth', move: 'Move', wan: 'WAN', factory_reset: 'Restore defaults',
  },
  panel: {
    title: 'Capabilities & warnings',
    reload: 'Refresh',
    retry: 'Try again',
    load_error: 'Could not read this OLT\'s capabilities. Buttons stay as usual.',
    model: 'Provisioning model',
    model_intent: 'by intent (whitelist + service, no line/service profiles)',
    model_profiles: 'by profiles (line-profile / service-profile)',
    classes: 'ONU classes',
    onuid_by_olt: 'The OLT picks the ONU-ID.',
    remove_clears: 'Removing an ONU erases its service and history on the OLT.',
    needs_save: 'Changes only persist after a save — the system schedules one ~5 min after the last write.',
    no_mass_ops: 'Mass operations (rebooting a PON/board, actions on all ONUs) do not exist for this OLT.',
    no_warnings: 'No warnings for the OLT\'s current state.',
    board_serial: 'Serial no.',
    board_roles: { master: 'Master', standby: 'Standby' },
    board_reboot_blocked: 'Rebooting a board is not done from here on this OLT.',
    auth_mode: 'Authentication',
    auth_modes: {
      whitelist: 'Serial whitelist', no_auth: 'No authentication (no_auth)', secret: 'Password/LOID',
      other: 'Other mode', unknown: 'Not read',
    },
  },
  warn: {
    pon_no_auth: { title: 'PON(s) in no_auth', action: 'On these PONs ONUs authorize themselves and controlled provisioning is unavailable. Put them in serial whitelist mode on the equipment — the system does not change the PON mode.' },
    pon_auth_needs_secret: { title: 'PON(s) in password/LOID mode', action: 'Not supported yet: serial authorization does not apply on these PONs.' },
    auto_save_off: { title: 'auto_save is off on the OLT', action: 'Changes only persist after a save, which the system schedules (~5 min after the last write).' },
    onu_alarms_off: { title: 'ONU deactivation alarms are off on the OLT', action: 'Drops raise no alarm on the equipment; the system detects them through polling.' },
    telnet_without_ssh: { title: 'CLI access over Telnet (unencrypted)', action: 'Prefer SSH when the equipment offers it, or use a VPN/tunnel.' },
    snmp_rw_missing: { title: 'SNMP write community missing', action: 'Renaming, rebooting and removing an ONU use the CLI (slower). Add the community in Edit OLT.' },
    pending_save: { title: 'Configuration not saved on the OLT yet', action: 'There are recent writes without a save. The system saves on its own ~5 min after the last write.' },
    driver_warning: { title: 'Driver warning', action: '' },
  },
  auth: {
    class_label: 'ONU class',
    class_names: { bridge: 'Bridge', hgu: 'HGU (routed)' },
    class_help: { bridge: 'Authorization type {type}: the ONU passes the service VLAN through.', hgu: 'Authorization type {type}: ONU with its own router/Wi-Fi.' },
    class_pick: 'Pick the class — it sets the authorization type written to the OLT.',
    class_detected: 'Suggested from the equipment detected by autofind: {class}.',
    vlan_label: 'Service VLAN',
    vlan_none: 'Pick the VLAN…',
    vlan_help: 'On FiberHome the VLAN is the ONU\'s service itself. Only VLANs allowed on this ONU\'s PON are listed.',
    vlan_load_error: 'Could not load the OLT\'s VLANs — authorization is blocked until they load.',
    mode_label: 'VLAN mode',
    mode_help: 'Tag: the OLT tags traffic with the VLAN. Transparent: passes it as it came.',
    mode_names: { tag: 'Tag', transparent: 'Transparent' },
    mode_default: 'Class default',
    mode_default_unknown: 'Class default (pick the class)',
    cos_label: 'COS (advanced)',
    cos_help: '802.1p priority from 0 to 7. Blank uses the class default.',
    cos_default: 'default: {n}',
    cos_default_unknown: 'class default',
    port_speed_label: 'Ethernet port speed',
    port_speed_help: 'Advanced. Blank leaves the ONU port alone.',
    port_speed_keep: 'Do not change',
    bw_download_label: 'Bandwidth — speed profile (download)',
    bw_upload_label: 'Bandwidth — speed profile (upload)',
    bw_note: 'The OLT keeps a single "service bandwidth" (FIX/ASSURE/MAX): with two different profiles the smaller one applies. Blank uses the tenant default.',
    bw_load_error: 'Could not load the speed profiles — the tenant default is still applied by the server.',
    onuid_label: 'ONU-ID',
    onuid_auto: 'Automatic',
    onuid_manual: 'Manual',
    onuid_auto_help: 'The OLT picks the first free ONU-ID on the PON (recommended).',
    onuid_manual_help: 'Use only if you need a specific ONU-ID (1 to 128). If it is taken, the OLT refuses and nothing is written.',
    save_note: 'The configuration was applied and confirmed on the OLT. The save (persistence) is scheduled by the system ~5 min after the last write.',
    preset_hidden: '{n} preset(s) from another vendor are hidden: this OLT is {vendor}.',
  },
  onu: {
    status_reasons: {
      los: 'LOS (optical signal lost)', dyinggasp: 'Dying-gasp (power loss)', offline: 'Offline',
      rebooting: 'rebooting…', 'auth-fail': 'Authentication failure', deauthorized: 'Disabled', other: 'Unexpected state',
    },
    equipment: 'Equipment',
    auth_type: 'Authorization type',
    auth_type_unknown: 'not read from the OLT yet',
    live_bw_note: 'The OLT refreshes traffic counters every {min} min — live bandwidth moves in steps, not in real time.',
    live_bw_waiting: 'Waiting for the next OLT counter step (up to {min} min) — the bandwidth shows up when it arrives.',
    delete_prompt: 'Removing erases the ONU\'s service and history on the OLT. Confirm?',
    delete_warning: 'Deleting removes the ONU\'s service and history on the OLT; to come back it must be authorized again.',
    move_unsupported: 'Moving an ONU does not exist on this OLT (removing erases the service): it is a new authorization.',
    replace_unsupported: 'Replacing the serial does not exist on this OLT: remove the ONU and authorize the new serial.',
    save_later: 'Changes on this OLT are saved on the equipment by the system ~5 min after the last write.',
  },
  services: {
    section: 'Services (VLAN / bandwidth)',
    open: 'Change VLAN / bandwidth',
    title: 'ONU services',
    heading: 'Change VLAN / bandwidth',
    loading: 'Reading the ONU services…',
    load_error: 'Could not read the ONU services.',
    not_supported: 'This OLT uses service-ports (profiles), not intent services.',
    class_inferred: 'inferred from type',
    type_line: 'Authorization type {type} · class {class}',
    no_service_read: 'No service read from the OLT for this ONU yet — run an OLT poll.',
    port: 'Port',
    vlan: 'Service VLAN',
    vlan_none: 'Pick the VLAN…',
    vlan_load_error: 'Could not load the OLT\'s VLANs.',
    vlan_out_of_rule: 'outside the PON rule',
    vlan_unknown: 'not in the OLT registry',
    mode: 'VLAN mode',
    mode_none: 'Pick the mode…',
    cos: 'COS',
    bw_title: 'Service bandwidth (FIX/ASSURE/MAX)',
    bw_short: 'Bandwidth',
    bw_note: 'In kbit/s. The OLT keeps one bandwidth per service; the direction (up/down) is not confirmed yet, so it is not split here.',
    bw_unsupported: 'This OLT does not declare support for changing bandwidth.',
    save_later: 'The OLT only persists the change after a save, which the system schedules ~5 min after the last write.',
    done: 'Services applied and confirmed on the OLT.',
    saving: 'Applying…',
    save: 'Apply',
    close: 'Close',
    applied_toast: 'Services of ONU {sn} applied',
  },
  auto: {
    move: 'Auto-Move does not exist on a FiberHome OLT ({olts}): removing the ONU erases its service and history. Use authorization.',
    resync: 'Auto-Resync does not exist on a FiberHome OLT ({olts}): rebuilding the configuration is a new authorization.',
  },
  preset: {
    vendor_label: 'Vendor',
    vendor_help: 'The preset only shows on OLTs of the same vendor (Huawei uses line/service profiles; FiberHome uses class, VLAN and mode).',
    class_help: 'Blank: the class comes from the ONU type or is chosen when authorizing.',
    class_auto: 'Choose when authorizing',
    vlan_help: 'Required to authorize with a preset: on FiberHome the VLAN is the ONU\'s service.',
    cos_invalid: 'Invalid COS — use an integer from 0 to 7.',
  },
};

const es = {
  olt: {
    telnet_warning: 'Telnet y SNMP v2c viajan sin cifrar — use una VPN/túnel (o SSH, si el equipo lo ofrece) entre OLTM y la OLT.',
    ssh_snmp_warning: 'SNMP v2c sigue sin cifrar incluso con SSH — use una VPN/túnel entre OLTM y la OLT.',
    dialect_label: 'Dialecto de la CLI',
    dialect_auto_hint: 'Detectado automáticamente en la identificación (Probar conexión). No hace falta elegir.',
    dialect_detected_hint: 'Detectado por el sistema al identificar la OLT.',
    rw_label: 'Comunidad SNMP de escritura',
    rw_configured: 'configurada',
    rw_will_set: 'se guardará al guardar',
    rw_will_remove: 'se eliminará al guardar',
    rw_remove_action: 'Quitar comunidad de escritura',
    rw_remove_undo: 'Deshacer quitar',
    rw_remove_confirm: '¿Quitar la comunidad de escritura? Renombrar, reiniciar y eliminar ONU usarán la CLI (más lenta). Solo se aplica al guardar.',
    rw_missing: 'no configurada',
    rw_hint: 'El valor nunca se muestra. Sin ella, renombrar, reiniciar y eliminar ONU usan la CLI (más lenta).',
    rw_form_hint: 'Opcional. Se usa para renombrar, reiniciar y eliminar ONU por SNMP; sin ella se usa la CLI (más lenta). No se crea en la OLT.',
    ro_form_hint: 'La comunidad de lectura que ya existe en la OLT (no se crea automáticamente).',
    model_unsupported: 'aún no soportado',
    model_unsupported_error: 'Este modelo FiberHome (AN6000) aún no está soportado — elija un AN5516/AN5116.',
    identity_title: 'Equipo identificado',
    identity_model: 'Modelo',
    identity_family: 'Familia',
    identity_firmware: 'Firmware',
    identity_dialect: 'Dialecto de la CLI',
    identity_serial: 'Nº de serie',
    identity_caps_after_save: 'Las capacidades del equipo aparecen después de guardar la OLT (Probar conexión en la pantalla de edición).',
    model_validated: 'validado',
    model_not_validated: 'compatible, no validado',
    model_not_validated_hint: 'Este modelo usa el mismo driver que el AN5516-01, pero nunca fue validado en un equipo real. Pruebe la conexión y siga el primer poll antes de autorizar ONUs.',
    pon_only_gpon: 'La integración FiberHome v1 soporta solo GPON (EPON y XG-PON quedan para una versión futura).',
    ssh_not_validated: 'SSH (no validado en FiberHome)',
    ssh_not_validated_hint: 'El driver acepta SSH, pero nunca fue validado en una OLT FiberHome real (el servidor SSH suele venir desactivado). Prefiera Telnet sobre un túnel/VPN.',
    enable_hint: 'El mismo usuario y contraseña entran al modo privilegiado (enable) — la OLT FiberHome usa la contraseña de login en el enable.',
    identity_replaces: 'Al guardar, el modelo y el firmware identificados reemplazan lo elegido arriba.',
    cli_probe_label: 'Login en la CLI',
    shell_unsupported: 'La terminal interactiva aún no está soportada para FiberHome (eludiría la lista de comandos permitidos). Use las acciones de la ONU/OLT y la auditoría de comandos.',
  },
  cap: {
    authorize: 'Autorizar', rename: 'Renombrar', reboot: 'Reiniciar', disable: 'Desactivar/Activar', delete: 'Eliminar',
    vlan: 'VLAN', bandwidth: 'Ancho de banda', move: 'Mover', wan: 'WAN', factory_reset: 'Restaurar valores',
  },
  panel: {
    title: 'Capacidades y avisos',
    reload: 'Actualizar',
    retry: 'Intentar de nuevo',
    load_error: 'No se pudieron leer las capacidades de esta OLT. Los botones siguen como siempre.',
    model: 'Modelo de aprovisionamiento',
    model_intent: 'por intención (whitelist + servicio, sin perfiles de línea/servicio)',
    model_profiles: 'por perfiles (line-profile / service-profile)',
    classes: 'Clases de ONU',
    onuid_by_olt: 'La OLT elige el ONU-ID.',
    remove_clears: 'Eliminar la ONU borra su servicio e historial en la OLT.',
    needs_save: 'Los cambios solo persisten tras un save — el sistema agenda uno ~5 min después de la última escritura.',
    no_mass_ops: 'Las operaciones masivas (reiniciar PON/placa, acciones sobre todas las ONU) no existen para esta OLT.',
    no_warnings: 'Sin avisos en el estado actual de la OLT.',
    board_serial: 'Nº de serie',
    board_roles: { master: 'Principal (master)', standby: 'Reserva (standby)' },
    board_reboot_blocked: 'Reiniciar la placa no se hace desde aquí en esta OLT.',
    auth_mode: 'Autenticación',
    auth_modes: {
      whitelist: 'Whitelist por serie', no_auth: 'Sin autenticación (no_auth)', secret: 'Con contraseña/LOID',
      other: 'Otro modo', unknown: 'No leído',
    },
  },
  warn: {
    pon_no_auth: { title: 'PON(s) en no_auth', action: 'En esas PON las ONU se autorizan solas y el aprovisionamiento controlado no está disponible. Póngalas en modo whitelist por serie en el equipo — el sistema no cambia el modo de la PON.' },
    pon_auth_needs_secret: { title: 'PON(s) en modo con contraseña/LOID', action: 'Aún no soportado: la autorización por serie no aplica en esas PON.' },
    auto_save_off: { title: 'auto_save apagado en la OLT', action: 'Los cambios solo persisten tras un save, que el sistema agenda (~5 min después de la última escritura).' },
    onu_alarms_off: { title: 'Alarmas de desactivación de ONU apagadas en la OLT', action: 'Las caídas no generan alarma en el equipo; el sistema las detecta por la recolección.' },
    telnet_without_ssh: { title: 'Acceso CLI por Telnet (sin cifrar)', action: 'Prefiera SSH cuando el equipo lo ofrezca, o use una VPN/túnel.' },
    snmp_rw_missing: { title: 'Falta la comunidad SNMP de escritura', action: 'Renombrar, reiniciar y eliminar ONU usan la CLI (más lenta). Registre la comunidad en Editar OLT.' },
    pending_save: { title: 'Configuración aún no guardada en la OLT', action: 'Hay escrituras recientes sin save. El sistema guarda solo ~5 min después de la última escritura.' },
    driver_warning: { title: 'Aviso del driver', action: '' },
  },
  auth: {
    class_label: 'Clase de la ONU',
    class_names: { bridge: 'Bridge', hgu: 'HGU (enrutada)' },
    class_help: { bridge: 'Tipo de autorización {type}: la ONU pasa la VLAN del servicio.', hgu: 'Tipo de autorización {type}: ONU con router/Wi-Fi propio.' },
    class_pick: 'Elija la clase — define el tipo de autorización grabado en la OLT.',
    class_detected: 'Sugerida por el equipo detectado en el autofind: {class}.',
    vlan_label: 'VLAN del servicio',
    vlan_none: 'Elija la VLAN…',
    vlan_help: 'En FiberHome la VLAN es el servicio mismo de la ONU. Solo aparecen las VLAN permitidas en la PON de esta ONU.',
    vlan_load_error: 'No se pudieron cargar las VLAN de la OLT — no se puede autorizar hasta que carguen.',
    mode_label: 'Modo de la VLAN',
    mode_help: 'Tag: la OLT marca el tráfico con la VLAN. Transparente: pasa tal como llegó.',
    mode_names: { tag: 'Tag', transparent: 'Transparente' },
    mode_default: 'Predeterminado de la clase',
    mode_default_unknown: 'Predeterminado de la clase (elija la clase)',
    cos_label: 'COS (avanzado)',
    cos_help: 'Prioridad 802.1p de 0 a 7. En blanco usa el predeterminado de la clase.',
    cos_default: 'predeterminado: {n}',
    cos_default_unknown: 'predeterminado de la clase',
    port_speed_label: 'Velocidad del puerto Ethernet',
    port_speed_help: 'Avanzado. En blanco no toca el puerto de la ONU.',
    port_speed_keep: 'No cambiar',
    bw_download_label: 'Ancho de banda — perfil de velocidad (download)',
    bw_upload_label: 'Ancho de banda — perfil de velocidad (upload)',
    bw_note: 'La OLT guarda un único "ancho de banda del servicio" (FIX/ASSURE/MAX): con dos perfiles distintos vale el menor. En blanco usa el predeterminado del tenant.',
    bw_load_error: 'No se pudieron cargar los perfiles de velocidad — el servidor aún aplica el predeterminado del tenant.',
    onuid_label: 'ONU-ID',
    onuid_auto: 'Automático',
    onuid_manual: 'Manual',
    onuid_auto_help: 'La OLT elige el primer ONU-ID libre de la PON (recomendado).',
    onuid_manual_help: 'Úselo solo si necesita un ONU-ID específico (1 a 128). Si está en uso, la OLT lo rechaza y no se graba nada.',
    save_note: 'La configuración se aplicó y se confirmó en la OLT. El save (persistencia) lo agenda el sistema ~5 min después de la última escritura.',
    preset_hidden: '{n} predefinición(es) de otro fabricante no aparecen: esta OLT es {vendor}.',
  },
  onu: {
    status_reasons: {
      los: 'LOS (pérdida de señal óptica)', dyinggasp: 'Dying-gasp (corte de energía)', offline: 'Fuera de línea',
      rebooting: 'reiniciando…', 'auth-fail': 'Fallo de autenticación', deauthorized: 'Desactivada', other: 'Estado inesperado',
    },
    equipment: 'Equipo',
    auth_type: 'Tipo de autorización',
    auth_type_unknown: 'aún no leído de la OLT',
    live_bw_note: 'La OLT actualiza los contadores de tráfico cada {min} min — el ancho de banda en vivo avanza por escalones, no en tiempo real.',
    live_bw_waiting: 'Esperando el próximo escalón del contador de la OLT (hasta {min} min) — el ancho de banda aparece cuando llegue.',
    delete_prompt: 'Eliminar borra el servicio y el historial de la ONU en la OLT. ¿Confirmar?',
    delete_warning: 'Eliminar quita el servicio y el historial de la ONU en la OLT; para volver hay que autorizarla de nuevo.',
    move_unsupported: 'Mover una ONU no existe en esta OLT (eliminar borra el servicio): es una nueva autorización.',
    replace_unsupported: 'Cambiar el serial no existe en esta OLT: elimine la ONU y autorice el nuevo serial.',
    save_later: 'Los cambios en esta OLT los guarda el sistema en el equipo ~5 min después de la última escritura.',
  },
  services: {
    section: 'Servicios (VLAN / ancho de banda)',
    open: 'Cambiar VLAN / ancho de banda',
    title: 'Servicios de la ONU',
    heading: 'Cambiar VLAN / ancho de banda',
    loading: 'Leyendo los servicios de la ONU…',
    load_error: 'No se pudieron leer los servicios de la ONU.',
    not_supported: 'Esta OLT usa service-ports (perfiles), no servicios por intención.',
    class_inferred: 'inferida del tipo',
    type_line: 'Tipo de autorización {type} · clase {class}',
    no_service_read: 'Aún no se leyó ningún servicio de la OLT para esta ONU — ejecute un poll de la OLT.',
    port: 'Puerto',
    vlan: 'VLAN del servicio',
    vlan_none: 'Elija la VLAN…',
    vlan_load_error: 'No se pudieron cargar las VLAN de la OLT.',
    vlan_out_of_rule: 'fuera de la regla de la PON',
    vlan_unknown: 'fuera del registro de la OLT',
    mode: 'Modo de la VLAN',
    mode_none: 'Elija el modo…',
    cos: 'COS',
    bw_title: 'Ancho de banda del servicio (FIX/ASSURE/MAX)',
    bw_short: 'Ancho de banda',
    bw_note: 'En kbit/s. La OLT guarda un ancho de banda por servicio; la dirección (subida/bajada) aún no está confirmada, por eso no se separa aquí.',
    bw_unsupported: 'Esta OLT no declara soporte para cambiar el ancho de banda.',
    save_later: 'La OLT solo persiste el cambio tras un save, que el sistema agenda ~5 min después de la última escritura.',
    done: 'Servicios aplicados y confirmados en la OLT.',
    saving: 'Aplicando…',
    save: 'Aplicar',
    close: 'Cerrar',
    applied_toast: 'Servicios de la ONU {sn} aplicados',
  },
  auto: {
    move: 'Auto-Mover no existe en una OLT FiberHome ({olts}): eliminar la ONU borra su servicio e historial. Use la autorización.',
    resync: 'Auto-Resincronizar no existe en una OLT FiberHome ({olts}): rehacer la configuración es una nueva autorización.',
  },
  preset: {
    vendor_label: 'Fabricante',
    vendor_help: 'La predefinición solo aparece en OLT del mismo fabricante (Huawei usa perfiles de línea/servicio; FiberHome usa clase, VLAN y modo).',
    class_help: 'En blanco: la clase viene del tipo de ONU o se elige al autorizar.',
    class_auto: 'Elegir al autorizar',
    vlan_help: 'Obligatoria para autorizar con predefinición: en FiberHome la VLAN es el servicio de la ONU.',
    cos_invalid: 'COS inválido — use un entero de 0 a 7.',
  },
};

export const FIBERHOME_I18N = { pt, en, es };
